import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import axios, { AxiosInstance } from 'axios'
import { authProfileId, clearAuthToken, clearTabAuthToken, readAuthToken, saveAuthToken, saveTabAuthToken, isAssistanceToken } from '../lib/authSession'
import { confirmDraftNavigation } from '../hooks/useUnsavedChangesWarning'
import { useModalDialog } from '../hooks/useModalDialog'
import { useLocale } from './LocaleContext'

export interface Organization { id: string; name: string; membershipId: string; roles: string[] }
interface User {
  id: string; email: string; name: string; tenantId?: string | null; tenant_id?: string | null
  organization?: { id: string; name: string }; permissions: string[]; is_super_admin?: boolean
  canManageBilling?: boolean; subscriptionTier: 'free' | 'pro' | 'enterprise'
  isImpersonating?: boolean
}
interface Selection { selectionToken: string; organizations: Organization[] }
interface PasswordChangeNotice { email: string; notificationSent: boolean }
interface AuthContextType {
  isAuthenticated: boolean; isLoading: boolean; user: User | null; error: string | null
  organizations: Organization[]; selection: Selection | null
  login: (email: string, password: string, organizationId?: string) => Promise<boolean>
  selectOrganization: (id: string) => Promise<boolean>; cancelSelection: () => void
  switchOrganization: (id: string, destination: string) => Promise<void>
  updateOrganizationName: (id: string, name: string) => void
  prepareInvitationSignIn: () => void
  prepareAccountSignIn: () => void
  completePasswordChange: (notice: PasswordChangeNotice) => void
  passwordChangeNotice: PasswordChangeNotice | null
  logout: () => Promise<void>; refreshToken: () => Promise<boolean>; apiClient: AxiosInstance
}
export const AuthContext = createContext<AuthContextType | undefined>(undefined)
const normalizeUser = (value: any): User => ({ ...value, tenantId: value.tenantId ?? value.tenant_id ?? null })
// Consume once at module load, before routing and network requests. Keeping it
// outside React initialization also makes StrictMode re-mounts safe.
const incomingAssistanceToken = (() => {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href), token = url.searchParams.get('impersonation_token')
  if (!token) return null
  url.searchParams.delete('impersonation_token'); window.history.replaceState(window.history.state, '', url)
  return token.length <= 8192 && isAssistanceToken(token) ? token : null
})()
function OrganizationSwitchProgress() {
  const { t } = useLocale()
  const dialog = useModalDialog({ onClose: () => {}, preventClose: true })
  return <dialog {...dialog} aria-label={t('Changement d’organisation', 'Switching organization')} aria-busy="true" tabIndex={-1} className="fixed inset-0 grid w-screen max-w-none place-items-center border-0 bg-black/40 p-5 backdrop:bg-black/40"><p role="status" className="rounded-xl bg-white p-6 text-slate-900 shadow-xl dark:bg-slate-900 dark:text-slate-100">{t('Ouverture de votre organisation…', 'Opening your organization…')}</p></dialog>
}
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [isAuthenticated, setIsAuthenticated] = useState(false), [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null), [error, setError] = useState<string | null>(null)
  const [organizations, setOrganizations] = useState<Organization[]>([]), [selection, setSelection] = useState<Selection | null>(null)
  const [switching, setSwitching] = useState(false)
  const [passwordChangeNotice, setPasswordChangeNotice] = useState<PasswordChangeNotice | null>(null)
  const generation = useRef(0), refreshing = useRef<Promise<boolean> | null>(null)
  const organizationRevision = useRef(0)
  const refreshRef = useRef<() => Promise<boolean>>(async () => false)
  const apiClient = useMemo(() => {
    const client = axios.create({ baseURL: import.meta.env.VITE_API_URL, withCredentials: true })
    client.interceptors.request.use(config => {
      const token = readAuthToken()
      if (token) config.headers.Authorization = `Bearer ${token}`
      ;(config as any)._sessionToken = token
      ;(config as any)._generation = generation.current
      return config
    })
    client.interceptors.response.use(response => {
      if ((response.config as any)._generation !== generation.current) throw new axios.CanceledError('Organization changed')
      return response
    }, async failure => {
      const request = failure.config
      if (!request || request._generation !== generation.current) throw new axios.CanceledError('Organization changed')
      if (failure.response?.status === 401 && request._sessionToken !== readAuthToken()) throw new axios.CanceledError('Session refreshed')
      const authenticationStep = /\/auth\/(login|refresh(?:-token)?|select-organization)(?:$|\?)/.test(request.url || '')
      if (failure.response?.status === 401 && request._sessionToken && !request._retry && !authenticationStep) {
        request._retry = true
        if (await refreshRef.current() && request._generation === generation.current) return client(request)
      }
      return Promise.reject(failure)
    })
    return client
  }, [])
  const reset = useCallback((thisTabOnly = false) => {
    generation.current++
    if (thisTabOnly) clearTabAuthToken(); else clearAuthToken()
    queryClient.clear()
    setUser(null); setIsAuthenticated(false); setOrganizations([]); setSelection(null)
  }, [queryClient])
  const prepareInvitationSignIn = useCallback(() => { reset(true); setError(null) }, [reset])
  const prepareAccountSignIn = useCallback(() => {
    reset(true); setPasswordChangeNotice(null); setError(null); setIsLoading(false)
  }, [reset])
  const completePasswordChange = useCallback((notice: PasswordChangeNotice) => { setPasswordChangeNotice(notice); reset(); setError(null); setIsLoading(false) }, [reset])
  const acceptSession = useCallback((data: any) => {
    if (!data.accessToken || !data.user?.id) throw new Error('Invalid session')
    generation.current++; saveAuthToken(data.accessToken, data.expiresIn); queryClient.clear()
    setUser(normalizeUser(data.user)); setOrganizations(data.organizations || [])
    setSelection(null); setIsAuthenticated(true); setError(null)
    setPasswordChangeNotice(null)
  }, [queryClient])
  const refreshToken = useCallback((): Promise<boolean> => {
    if (!readAuthToken()) return Promise.resolve(false)
    if (isAssistanceToken(readAuthToken())) return Promise.resolve(false)
    if (refreshing.current) return refreshing.current
    const version = generation.current
    refreshing.current = (async () => {
      try {
        const response = await apiClient.post('/auth/refresh', { expectedUserId: authProfileId() })
        if (generation.current !== version) return false
        saveAuthToken(response.data.accessToken, response.data.expiresIn)
        return true
      } catch {
        if (generation.current === version) reset()
        return false
      } finally { refreshing.current = null }
    })()
    return refreshing.current
  }, [apiClient, reset])
  refreshRef.current = refreshToken
  useEffect(() => {
    let active = true
    const version = generation.current
    void (async () => {
      try {
        if (incomingAssistanceToken) saveTabAuthToken(incomingAssistanceToken)
        if (!readAuthToken()) return
        const expiry = Number(sessionStorage.getItem('tokenExpiry'))
        if (expiry && expiry < Date.now() && !await refreshToken()) return
        const response = await apiClient.get('/auth/me')
        const current = response.data.user ?? response.data
        if (incomingAssistanceToken && current?.isImpersonating !== true) throw new Error('Invalid assistance session')
        if (active && generation.current === version && current?.id) {
          setUser(normalizeUser(current)); setOrganizations(response.data.organizations || []); setIsAuthenticated(true)
        }
      } catch (failure) {
        if (active && !axios.isCancel(failure)) { reset(); setError('Impossible de vérifier votre session. Veuillez vous reconnecter.') }
      } finally { if (active) setIsLoading(false) }
    })()
    return () => { active = false }
  }, [apiClient, refreshToken, reset])
  const login = useCallback(async (email: string, password: string, organizationId?: string) => {
    setError(null); setIsLoading(true)
    try {
      const response = await apiClient.post('/auth/login', { email, password, scope: 'optimizer', organizationId })
      if (response.data.organizationSelectionRequired) {
        reset(); setSelection({ selectionToken: response.data.selectionToken, organizations: response.data.organizations })
        return false
      }
      acceptSession(response.data); return true
    } catch (failure) {
      setError(axios.isAxiosError(failure) ? failure.response?.data?.message || 'Connexion impossible. Réessayez.' : 'Connexion impossible.')
      return false
    } finally { setIsLoading(false) }
  }, [apiClient, acceptSession, reset])
  const selectOrganization = useCallback(async (organizationId: string) => {
    if (!selection) return false
    setIsLoading(true); setError(null)
    try {
      const response = await apiClient.post('/auth/select-organization', { selectionToken: selection.selectionToken, organizationId })
      acceptSession(response.data); return true
    } catch (failure) {
      setError(axios.isAxiosError(failure) ? failure.response?.data?.message || 'Sélection impossible. Reconnectez-vous.' : 'Sélection impossible.')
      return false
    } finally { setIsLoading(false) }
  }, [apiClient, acceptSession, selection])
  const cancelSelection = useCallback(() => { setSelection(null); setError(null) }, [])
  const switchOrganization = useCallback(async (organizationId: string, destination: string) => {
    if (queryClient.isMutating()) throw new Error('Attendez la fin des opérations en cours avant de changer d’organisation.')
    setSwitching(true)
    try {
      const response = await apiClient.post('/auth/switch-organization', { organizationId }, { timeout: 20_000 })
      await queryClient.cancelQueries()
      confirmDraftNavigation()
      setIsLoading(true)
      acceptSession(response.data)
      window.location.replace(destination)
    } catch (failure) {
      setSwitching(false)
      throw new Error(axios.isAxiosError(failure) ? failure.response?.data?.message || 'Impossible de changer d’organisation. Réessayez.' : 'Impossible de changer d’organisation. Réessayez.')
    }
  }, [apiClient, queryClient, acceptSession])
  const logout = useCallback(async () => {
    if (isAssistanceToken(readAuthToken())) { reset(true); setError(null); return }
    try { await apiClient.post('/auth/logout') } finally { reset(); setError(null) }
  }, [apiClient, reset])
  const updateOrganizationName = useCallback((id: string, name: string) => {
    organizationRevision.current++
    setUser(current => current && String(current.tenantId ?? current.tenant_id) === id ? { ...current, organization: { id, name } } : current)
    setOrganizations(current => current.map(organization => organization.id === id ? { ...organization, name } : organization))
  }, [])
  useEffect(() => {
    if (!isAuthenticated) return
    let active = true, running = false
    const refresh = async () => {
      if (running || document.visibilityState === 'hidden') return
      running = true
      const version = generation.current
      const revision = organizationRevision.current
      try {
        const response = await apiClient.get('/auth/me')
        const current = response.data.user ?? response.data
        if (active && version === generation.current && revision === organizationRevision.current && current?.id) {
          setUser(normalizeUser(current)); setOrganizations(response.data.organizations || [])
        }
      } catch (failure) {
        if (active && version === generation.current && axios.isAxiosError(failure) && [401, 403].includes(failure.response?.status || 0)) reset()
      } finally { running = false }
    }
    const listener = () => { void refresh() }
    const timer = window.setInterval(listener, 30_000)
    window.addEventListener('focus', listener); window.addEventListener('auth-permissions-changed', listener)
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', listener); window.removeEventListener('auth-permissions-changed', listener) }
  }, [apiClient, isAuthenticated, reset])
  return <AuthContext.Provider value={{ isAuthenticated, isLoading, user, error, organizations, selection, login, selectOrganization, cancelSelection, switchOrganization, updateOrganizationName, prepareInvitationSignIn, prepareAccountSignIn, completePasswordChange, passwordChangeNotice, logout, refreshToken, apiClient }}>{children}{switching && <OrganizationSwitchProgress />}</AuthContext.Provider>
}
